import { DateTime, Interval } from "luxon";
import React, { useMemo, useRef } from "react";

import { CopyText } from "@clockwise/web-commons/src/ui/copy-text/CopyText";
import { TrackingEvents, useTrackOnce } from "@clockwise/web-commons/src/util/analytics.util";
import { isEmpty } from "lodash";
import { formattedDateTime } from "../../../../web-app-calendar/calendar-popover/utils/formattedDateTime";
import {
  GQLConsequencesBlock,
  GQLOthersConsequenceDiffSummary,
  GQLYourConsequenceDiffSummary,
} from "../../utils/types";

interface ICopyTimesPreviewProps {
  title: string;
  viewerGivenName: string | null;
  selectedTime: DateTime | null;
  isReschedule: boolean;
  isProposeNewTime: boolean;
  consequencesBlock: GQLConsequencesBlock | null;
  sharedProposalLink: string;
  targetTimeZone: string;
  onCopyRichText: (text: string) => void;
}

const LinkComponent = ({ link, linkTitle }: { link: string; linkTitle: string }) => {
  return (
    <span className="cw-font-seminbold cw-no-underline">
      <a href={link} target="_blank" rel="noreferrer">
        {linkTitle}
      </a>
    </span>
  );
};

const CopyTimesRow = ({
  link,
  calDiff,
  targetTimeZone,
}: {
  link?: string;
  calDiff: GQLYourConsequenceDiffSummary | GQLOthersConsequenceDiffSummary;
  targetTimeZone: string;
}) => {
  const time = Interval.fromISO(calDiff.updatedTime || calDiff.currentTime).start;
  return (
    <li>
      {link ? (
        <LinkComponent link={link} linkTitle={calDiff.title} />
      ) : (
        <span className="cw-font-seminbold">{calDiff.title}</span>
      )}
      {" -> "}
      {formattedDateTime(time, targetTimeZone, true)}
    </li>
  );
};

export const ProposalCopyTimesPreview = ({
  title,
  viewerGivenName,
  selectedTime,
  isReschedule,
  isProposeNewTime,
  consequencesBlock,
  sharedProposalLink,
  targetTimeZone,
  onCopyRichText,
}: ICopyTimesPreviewProps) => {
  const trackCopyTimes = useTrackOnce(TrackingEvents.SHAREABLE_PROPOSAL.MODAL.COPY_TIMES);
  const copyRef = useRef<HTMLDivElement>(null);
  const formattedTime = selectedTime ? formattedDateTime(selectedTime, targetTimeZone, true) : null;
  const hasConsequences =
    !isEmpty(consequencesBlock?.yourCalDiffs) || !isEmpty(consequencesBlock?.otherCalDiffs);

  const handleCopyTimesClicked = () => {
    if (copyRef.current) {
      onCopyRichText(copyRef.current.innerHTML);
      trackCopyTimes();
    }
  };

  const copyableRichText = useMemo(() => {
    return (
      <div ref={copyRef}>
        <div>
          {isReschedule ? "Can we move" : "Are you free to meet for"}{" "}
          <span className="cw-font-seminbold">{title}</span>
          {formattedTime && !isReschedule && ` - ${formattedTime}?`}
          {formattedTime && isReschedule && ` to ${formattedTime}?`}
          {isProposeNewTime && " I would do it, but I don't have edit rights."}
        </div>
        {hasConsequences && (
          <div className="cw-mt-3">
            <div>There are a few issues with that time, which Clockwise will fix if it can:</div>
            <ul
              className="cw-list-inside cw-list-disc cw-ml-3 cw-mt-3"
              aria-labelledby="best-days-and-times"
            >
              {(consequencesBlock?.yourCalDiffs || []).map((calDiff) => (
                <CopyTimesRow key={calDiff.id} calDiff={calDiff} targetTimeZone={targetTimeZone} />
              ))}
              {(consequencesBlock?.otherCalDiffs || []).map((calDiff) => (
                <CopyTimesRow key={calDiff.id} calDiff={calDiff} targetTimeZone={targetTimeZone} />
              ))}
            </ul>
          </div>
        )}
        <div className="cw-mt-3">Does that work?</div>
        <div className="cw-flex cw-flex-row cw-items-center cw-gap-2">
          🔗{" "}
          <a
            href={`${sharedProposalLink}&cw_link_source=view_in_clockwise`}
            target="_blank"
            rel="noreferrer"
            className="cw-text-[blue]"
          >
            {viewerGivenName ? `Respond to ${viewerGivenName}` : "Respond in Clockwise"}
          </a>
        </div>
      </div>
    );
  }, [
    consequencesBlock,
    sharedProposalLink,
    targetTimeZone,
    title,
    formattedTime,
    isReschedule,
    isProposeNewTime,
    hasConsequences,
  ]);

  return (
    <div className="cw-leading-5 cw-flex cw-flex-col cw-gap-3 cw-text-muted cw-cursor-default">
      <CopyText onCopy={handleCopyTimesClicked} copyAriaLabel="copy available times">
        {copyableRichText}
      </CopyText>
    </div>
  );
};
